import {useProfileStatus} from "../../profile-status/hooks";
import {useKycStatus} from "./useKycStatus";
import {KycRecord} from "../domain/kyc.entity";

// ==================== Types ====================

export type KycDisplayStatus = "no_entry" | "pending" | "successful" | "rejected";

export interface UseKycDisplayStatusResult {
  displayStatus: KycDisplayStatus;
  rejectionReason: string | null;
  isLoading: boolean;
  kycRecord: KycRecord | null;
}

// ==================== Hook ====================

export function useKycDisplayStatus(): UseKycDisplayStatusResult {
  const profileStatusQuery = useProfileStatus();
  const kycVerified = profileStatusQuery.data?.kycVerified ?? false;

  const kycStatusQuery = useKycStatus(!kycVerified);

  if (kycVerified) {
    return {
      displayStatus: "successful",
      rejectionReason: null,
      isLoading: profileStatusQuery.isLoading,
      kycRecord: null,
    };
  }

  const kycRecord = kycStatusQuery.data ?? null;
  const displayStatus = deriveDisplayStatus(kycRecord);
  const rejectionReason = kycRecord?.reasonForRejection ?? null;

  return {
    displayStatus,
    rejectionReason,
    isLoading: profileStatusQuery.isLoading || kycStatusQuery.isLoading,
    kycRecord,
  };
}

// ==================== Helpers ====================

function deriveDisplayStatus(kycRecord: KycRecord | null): KycDisplayStatus {
  if (!kycRecord) {
    return "no_entry";
  }

  if (kycRecord.isFailed()) {
    return "rejected";
  }

  // "InProgress" with a rejection reason means admin sent the record back
  // for changes — show the rejected UI so the user sees the reason and CTA.
  // "InProgress" without a reason is a fresh draft — drop into the form.
  if (kycRecord.isInProgress()) {
    return kycRecord.reasonForRejection ? "rejected" : "no_entry";
  }

  if (kycRecord.isSubmitted()) {
    return "pending";
  }

  if (kycRecord.isActive()) {
    return "successful";
  }

  return "no_entry";
}
